import { Container, Modal } from "react-bootstrap";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { fetchProjectEpicsAndSprints, getProjectMembers, importTaskByWorklistId } from "../../../services/managerWorkListService";
import { useEffect, useState } from "react";
import { sprintStatusIdConstants } from "../../../utils/constants";


const WorklistImportTasksModal = ({ showImportTaskModal, CloseImportTaskModal, worklistId, setDependencyForFetchWorklistProgressRerender, setSuccess }) => {
  const [epics, setEpics] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [taskFileInput, setTaskFileInput] = useState(null);
  const [recordsCountInFile, setRecordCountInFile] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);

  const [importApiResponse, setImportApiResponse] = useState(null);

  const {register, handleSubmit, control, reset, setValue, getValues} = useForm();

  const fetchProjectMembers = async () => {
    const response = await getProjectMembers(worklistId);
    if (response) {
      setProjectMembers(response?.data);
    }
  };

  const onSave = async (data) => {
    const sprintId = data?.sprint_id?.value || null;
    const epicId = data?.epic_id || null;
    
    if (!taskFileInput || !sprintId) {
      alert("Please fill out all fields and upload a file!");
      return;
    }
    
    const formData = {
        file: taskFileInput,
        sprint_id: sprintId,
        epic_id: epicId || null,
    }

    try {
      const response = await importTaskByWorklistId(worklistId, formData);

      // Extract the base64 string from the response
      const { data } = response;

      if (data?.excel) {
        // Decode the base64 string to a binary Blob
        const binaryData = atob(data?.excel);
        const byteNumbers = new Uint8Array(binaryData.length);

        for (let i = 0; i < binaryData.length; i++) {
          byteNumbers[i] = binaryData.charCodeAt(i);
        }
        
        const blob = new Blob([byteNumbers], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        // Create a download link and trigger the download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "task-list.xlsx";
        link.click();

        setImportApiResponse(response.data);
        setDependencyForFetchWorklistProgressRerender((prev)=> !prev);
        CloseImportTaskModal();
        setSuccess(prev => !prev);
        window.toastr.success("Task import status: Please verify from the downloaded file.");
      }

    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file!");
    }
  };

  const handleTaskInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = () => setTaskFileInput(reader.result); // Base64 encoded file
      reader.onerror = (error) => console.error("Error reading file:", error);
    }
  }

  const resetTask = () => {
    reset({
      Task: "",
      userId: "",
      start_date: "",
      end_date: "",
      priority_id: "",
      description: "",
    });
  };

  const renderSprints = () => {
    const groupedOptions = [];
    const { IN_PROGRESS_STATUS_ID } = sprintStatusIdConstants;
    if (sprints.length !== 0) {
      sprints.forEach((sprint) => {
        const group_name = sprint.SprintStatusId === IN_PROGRESS_STATUS_ID ? "Active Sprints" : "Inactive Sprints";
        const group = groupedOptions.find((group) => group.label === group_name);
        if (group) {
          group_name === "Inactive Sprints" &&
            group.options.push({
              label: sprint.label,
              value: sprint.value,
            });
          group_name === "Active Sprints" &&
            group.options.unshift({
              label: sprint.label,
              value: sprint.value,
            });
        } else {
          group_name === "Inactive Sprints" &&
            groupedOptions.push({
              label: group_name,
              options: [
                {
                  label: sprint.label,
                  value: sprint.value,
                },
              ],
            });
          group_name === "Active Sprints" &&
            groupedOptions.unshift({
              label: group_name,
              options: [
                {
                  label: sprint.label,
                  value: sprint.value,
                },
              ],
            });
        }
      });
    }
    return groupedOptions;
  };

  const customStyles = {
    groupHeading: (provided) => ({
      ...provided,
      fontWeight: "bold",
      fontSize: "14px",
      color: "var(--bs-heading-color, inherit) !important",
    }),
  };

  useEffect(() => {
    fetchProjectMembers();
    fetchProjectEpicsAndSprints(worklistId).then((response) => {
      setEpics(response?.data?.epics ?? []);
      setSprints(response?.data?.sprints ?? []);
      if (response?.data?.active_sprint) {
        setValue("sprint_id", { value: response?.data?.active_sprint?.value, label: response?.data?.active_sprint?.label });
      }
    });
  }, [setValue, worklistId]);

  return (
    <>
      <Container>
        <Modal show={showImportTaskModal} size="lg" centered>
          <div>
            <div>
              <Modal.Header className="modal-header py-4">
                <h2 className="fw-bold">Import Tasks</h2>
                <Modal.Title
                  className="btn btn-icon btn-sm btn-active-icon-primary"
                  onClick={() => {
                    CloseImportTaskModal();
                    resetTask();
                  }}
                >
                  <span className="svg-icon svg-icon-1">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor" />
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor" />
                    </svg>
                  </span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="modal-body my-5">
                <form onSubmit={handleSubmit(onSave)}>
                  <div style={{ maxHeight: "510px", overflowY: "auto", overflowX: "hidden" }}>
                    <div className="row mb-4">
                      <div className="col-xl-4 col-lg-4">
                        <label className="form-label">Sprint</label>
                        <Controller name="sprint_id" control={control} rules={{}} render={({ field }) => <Select {...field} options={renderSprints()} groupBy="label" styles={customStyles} />} />
                      </div>
                      <div className="col-xl-4 col-lg-4">
                        <label className="form-label">Epic</label>
                        <select className="form-select mb-2" data-control="select2" data-hide-search="true" name="epic_id" {...register("epic_id")}>
                          <option value="">---</option>
                          {epics?.map((option) => (
                            <option key={option?.value} value={option?.value}>
                              {option?.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                      <fieldset>
                        <legend>Import File</legend>
                        <div className="card card-flush mb-4">
                          <div className="card-body pt-0">
                            <div className="form-group mt-5">
                                  <input onChange={handleTaskInputChange} type="file" accept=".xlsx, .xls" data-repeater-create="" className="btn btn-sm btn-light"/>
                            </div>
                            {
                                recordsCountInFile && <p className="mt-2">Total tasks found: <span className="fw-bold">{recordsCountInFile || 0}</span></p>
                            }
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    </div>
                  </div>
                  <div className="text-center pt-8">
                    <button
                      type="reset"
                      className="btn btn-light me-3"
                      data-kt-users-modal-action="cancel"
                      onClick={(e) => {
                        CloseImportTaskModal();
                        resetTask();
                      }}
                    >
                      Cancel
                    </button>
                    <button disabled={taskFileInput ? false : true} type="submit" className="btn btn-primary me-4" id="savebutton">
                      <span className="indicator-label">Save Tasks</span>
                      <span className="indicator-progress">
                        Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    </button>
                  </div>
                </form>
              </Modal.Body>
            </div>
          </div>
        </Modal>
      </Container>
    </>
  );
};
export default WorklistImportTasksModal;
